import type { Params, RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { appConfig, Environment } from '@app/config/appConfig';
import { Login } from '@features/authentication/pages/Login';
import { UpdatePasswordPage } from '@features/authentication/pages/UpdatePasswordPage';
import { InquiryFindingsBreadcrumb } from '@features/inquiry-findings/components/InquiryFindingsBreadcrumb';
import { InsightFindingsBreadcrumb } from '@features/insight-findings/components/InsightFindingsBreadcrumb';
import { InvestigationFindingsBreadcrumb } from '@features/investigation-findings/components/InvestigationFindingsBreadcrumb';
import { GlobalErrorPage, GlobalLayout, NotFoundPage, UnauthorizedPage } from '@shared/layouts';
import { LoadingPageSpinner } from '@shared/overlays';

import { App } from '../../App';
import { urls } from '../urls';

import { AuthProtectedRoute } from './AuthProtectedRoute';
import { PublicOnlyRoute } from './PublicOnlyRoute';

export const routes: RouteObject[] = [
  {
    element: <GlobalLayout />,
    errorElement: <GlobalErrorPage />,
    children: [
      {
        path: '/',
        hydrateFallbackElement: <LoadingPageSpinner />,
        element: (
          <AuthProtectedRoute>
            <App />
          </AuthProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to={urls.results.path} /> },
          {
            path: urls.results.path,
            lazy: () => import('../../features/investigations/pages/FilesPage'),
          },
          {
            path: urls.assets.path,
            lazy: () => import('../../features/asset-management/pages/AssetManagementPage'),
          },
          {
            path: urls.applications.path,
            lazy: () => import('../../features/applications/pages/ApplicationsPage'),
          },
          {
            path: urls.tokenLedger.path,
            lazy: () => import('@features/token-ledger/pages/TokenLedgerPage'),
          },
          ...(appConfig.environment === Environment.PROD
            ? []
            : [
                {
                  path: urls.integrations.path,
                  lazy: () => import('../../features/integrations/pages/IntegrationsPage'),
                },
                {
                  path: urls.manage.path,
                  lazy: () => import('@features/integrations/pages/ManageIntegrationsPage'),
                },
                {
                  path: urls.reports.path,
                  lazy: () => import('@features/reports/pages/ReportsPage'),
                },
              ]),
          {
            path: urls.userManagement.path,
            lazy: () => import('../../features/user-management/pages/UserManagementPage'),
          },
          {
            path: urls.traceFindings.path,
            lazy: () => import('../../features/investigation-findings/pages/InvestigationFindingsPage'),
            handle: {
              crumb: (params: Params<string>) => <InvestigationFindingsBreadcrumb traceId={params.traceId} />,
            },
          },
          {
            path: urls.inquiryFindings.path,
            lazy: () => import('../../features/inquiry-findings/pages/InquiryFindingsPage'),
            handle: {
              crumb: (params: Params<string>) => <InquiryFindingsBreadcrumb inquiryId={params.inquiryId} />,
            },
          },
          {
            path: urls.insightFindings.path,
            lazy: () => import('../../features/insight-findings/pages/InsightFindingsPage'),
            handle: {
              crumb: (params: Params<string>) => <InsightFindingsBreadcrumb insightId={params.insightId} />,
            },
          },
          {
            path: urls.manageApiKeys.path,
            lazy: () => import('../../features/api-keys/pages/ApiKeysPage'),
          },
          {
            path: urls.auditLog.path,
            lazy: () => import('../../features/audit-log/pages/AuditLogPage'),
          },
        ],
      },
      {
        path: urls.login.path,
        element: (
          <PublicOnlyRoute>
            <Login />
          </PublicOnlyRoute>
        ),
      },
      { path: urls.updatePassword.path, element: <UpdatePasswordPage /> },
      { path: '/unauthorized', element: <UnauthorizedPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
